import type { FundAnalysis } from './analytics'

export type ActionPlan = {
  title: string
  tone: 'mint' | 'amber' | 'rose'
  newPosition: string
  existingPosition: string
  explanation: string
}

/**
 * The same fail-closed action mapping used on the fund detail view. Keeping it
 * here lets the buy-signal screen and the detail view agree on what a current
 * buy signal actually means.
 */
export function actionPlan(analysis: FundAnalysis): ActionPlan {
  const recommendation = analysis.recommendation.toLowerCase()
  const goodQuality = recommendation === 'strong candidate' || recommendation === 'buy candidate'
  const currentWeekMissing = analysis.decisionGuard?.status === 'caution' && analysis.decisionGuard.reasons.some((reason) => reason.startsWith('The latest 1W NAV return is not loaded'))
  if (analysis.decisionGuard?.status === 'blocked') {
    return { title: 'NO NEW BUY', tone: 'rose', newPosition: 'DO NOT BUY', existingPosition: 'HOLD / REVIEW', explanation: analysis.decisionGuard.reasons[0] || 'Current signals are weak enough to block a new position until the trend stabilizes. This is not an automatic sell signal.' }
  }
  if (currentWeekMissing) {
    return { title: 'CHECK CURRENT DATA', tone: 'amber', newPosition: 'OPEN TO VERIFY', existingPosition: 'HOLD / MONITOR', explanation: 'The automatic universe does not include this fund’s latest weekly signal yet. Open the fund to fetch the direct TEFAS snapshot before deciding on new money.' }
  }
  if (analysis.decisionGuard?.status === 'caution') {
    return { title: 'WAIT — NOT A FRESH BUY', tone: 'amber', newPosition: 'WAIT / DO NOT BUY', existingPosition: 'HOLD / MONITOR', explanation: analysis.decisionGuard.reasons[0] || 'The fund may still be structurally strong, but current conditions do not justify chasing a new position.' }
  }
  if (goodQuality && analysis.entryScore >= 70) {
    return { title: 'BUY CANDIDATE', tone: 'mint', newPosition: 'BUY GRADUALLY', existingPosition: 'HOLD / ADD GRADUALLY', explanation: 'The fund and the current entry setup both look supportive. The engine still favors staged buying over an aggressive one-time entry.' }
  }
  if (goodQuality) {
    return { title: 'WAIT FOR A BETTER ENTRY', tone: 'amber', newPosition: 'WAIT / SMALL START', existingPosition: 'HOLD', explanation: 'The fund looks good, but the current entry setup is not strong enough to justify chasing it. Existing holders have a stronger case to stay patient.' }
  }
  if (recommendation === 'hold' || recommendation === 'neutral') {
    return { title: 'HOLD / WAIT', tone: 'amber', newPosition: 'WAIT — DON’T BUY', existingPosition: 'HOLD', explanation: 'The indicators do not show a clear enough edge for a new purchase. If you already own it, the current evidence supports monitoring rather than an automatic exit.' }
  }
  if (recommendation === 'reduce' || recommendation === 'avoid new position') {
    return { title: 'REDUCE RISK', tone: 'rose', newPosition: 'DON’T BUY', existingPosition: 'REDUCE', explanation: 'The setup is weak relative to the available alternatives. New money should stay out; existing holders should consider trimming if the thesis is no longer strong.' }
  }
  return { title: 'EXIT REVIEW', tone: 'rose', newPosition: 'AVOID', existingPosition: 'REDUCE / REVIEW EXIT', explanation: 'Several automatic signals are weak enough to review the position. This is an analytical exit review, not a guarantee that prices will fall.' }
}

/** A signal is shown only when the same action layer would say BUY CANDIDATE. */
export function isBuySignal(analysis: FundAnalysis): boolean {
  return actionPlan(analysis).title === 'BUY CANDIDATE'
}

